import { useEffect, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { create } from "zustand";
import { Button, EmptyState, Spinner, toast } from "@/ui";
import { useSession } from "./session";
import { invokeEngine, invalidateEngine } from "@/engine/hooks";
import { platformForHost } from "./session";
import { useTheme } from "./theme";

interface EngineStatus {
  status: "idle" | "booting" | "ready" | "error";
  error: string | null;
  bootedFor: string | null;
  set(patch: Partial<EngineStatus>): void;
}
export const useEngineStatus = create<EngineStatus>((set) => ({ status: "idle", error: null, bootedFor: null, set: (p) => set(p) }));

/** Calls engine `init` for the current session and re-sends the token when it changes. */
export function useEngineBoot() {
  const workspaceId = useSession((s) => s.workspaceId);
  const userId = useSession((s) => s.user?.id ?? null);
  const deviceId = useSession((s) => s.deviceId);
  const serverUrl = useSession((s) => s.serverUrl);
  const token = useSession((s) => s.token);
  const offline = useSession((s) => s.offline);
  const status = useEngineStatus();

  useEffect(() => {
    if (!workspaceId || !userId) return;
    const key = `${workspaceId}:${userId}:${offline ? "offline" : serverUrl}`;
    if (status.bootedFor === key && status.status !== "error") return;
    status.set({ status: "booting", error: null, bootedFor: key });
    invokeEngine("init", { workspaceId, userId, deviceId, serverUrl: offline ? null : serverUrl, token: offline ? null : token, platform: platformForHost(), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? null })
      .then(async () => {
        status.set({ status: "ready" });
        invalidateEngine();
        await adoptPendingLocalWorkspace(workspaceId);
        try {
          const settings = await invokeEngine("getUserSettings", undefined);
          useTheme.getState().hydrateFromSettings(settings);
        } catch {
          // settings are optional
        }
      })
      .catch((e: unknown) => status.set({ status: "error", error: e instanceof Error ? e.message : String(e) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, userId, deviceId, serverUrl, offline]);

  useEffect(() => {
    if (status.status !== "ready" || offline) return;
    invokeEngine("setToken", { token }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
}

export function RequireSession({ children }: { children: ReactNode }) {
  const isSignedIn = useSession((s) => !!s.user && !!s.workspaceId);
  const onboarded = useSession((s) => s.onboarded);
  const engine = useEngineStatus();
  const location = useLocation();
  const navigate = useNavigate();

  // Extension deep links arrive as ?contactId=; honour them once. /ask?contactId= is the Ask page's own context, not a deep link.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const contactId = params.get("contactId");
    if (contactId && !location.pathname.startsWith("/contact/") && !location.pathname.startsWith("/ask")) navigate(`/contact/${contactId}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isSignedIn) return <Navigate to="/onboarding" replace state={{ from: location.pathname }} />;
  if (!onboarded && location.pathname === "/" && !location.search.includes("skipOnboarding")) return <Navigate to="/onboarding" replace />;
  if (engine.status === "error") {
    return (
      <EmptyState title="The local engine did not start">
        <div style={{ maxWidth: 420 }}>{engine.error}</div>
        <div className="u-row" style={{ marginTop: 12, justifyContent: "center" }}>
          <Button onClick={() => window.location.reload()}>Reload</Button>
          <Button variant="outline" onClick={() => { const u = new URL(window.location.href); u.searchParams.set("engine", "mock"); window.location.href = u.toString(); }}>Use demo data</Button>
        </div>
      </EmptyState>
    );
  }
  if (engine.status !== "ready") {
    return (
      <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, color: "var(--text-secondary)" }}>
        <Spinner /> Opening your workspace
      </div>
    );
  }
  return <>{children}</>;
}

/**
 * After signing in from offline mode, or into an account on another server, folds the workspace left behind into
 * the new one once. Only an empty workspace takes the data by itself; one that already has people keeps them apart,
 * and Settings offers the move for that case.
 */
async function adoptPendingLocalWorkspace(workspaceId: string): Promise<void> {
  const session = useSession.getState();
  const from = session.pendingLocalWorkspaceId;
  if (!from || from === workspaceId || session.offline) return;
  try {
    const local = await invokeEngine("listLocalWorkspaces", undefined);
    const source = local.find((w) => w.id === from);
    const target = local.find((w) => w.id === workspaceId);
    if (!source || source.contacts === 0) {
      session.clearPendingLocalWorkspace();
      return;
    }
    if (target && target.contacts > 0) {
      toast(`${source.contacts} people from your previous workspace are still on this device. Settings can move them into this one.`);
      return;
    }
    const r = await invokeEngine("adoptWorkspace", { fromWorkspaceId: from });
    toast(`Brought ${r.contacts} people from your previous workspace into this one`, "success");
    invalidateEngine();
  } catch (e) {
    toast(e instanceof Error ? e.message : "Could not move your previous data", "danger");
    return;
  }
  session.clearPendingLocalWorkspace();
}
